/**
 * Audits Dashboard - Enterprise Grade
 * Comprehensive audit management and tracking
 */

import React, { useState, useEffect } from 'react';
import {
  ClipboardList, CheckCircle, Clock, Calendar, Download, Plus,
  Eye, FileText, TrendingUp, Target, AlertTriangle, Filter,
  Search, Award, XCircle, ArrowRight, Settings
} from 'lucide-react';
import { auditData } from '../../data/mockData';
import { useAppContext } from '../../context/AppContext';
import { useToast } from '../Common/ToastProvider';

const AuditsDashboard = () => {
  const { state } = useAppContext();
  const { showToast } = useToast();
  const selectedFacility = state.selectedFacility;
  
  const [audits, setAudits] = useState([]);
  const [filteredAudits, setFilteredAudits] = useState([]);
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, [selectedFacility]);

  useEffect(() => {
    filterData();
  }, [audits, filterStatus, filterCategory, searchTerm]);

  const loadData = () => {
    setLoading(true);
    const facilityAudits = auditData.filter(a => a.facilityId === selectedFacility);
    setAudits(facilityAudits);
    setLoading(false);
  };

  const filterData = () => {
    let filtered = [...audits];
    
    if (filterStatus !== 'all') {
      filtered = filtered.filter(a => a.status.toLowerCase() === filterStatus.toLowerCase());
    }
    
    if (filterCategory !== 'all') {
      filtered = filtered.filter(a => a.category === filterCategory);
    }
    
    if (searchTerm) {
      filtered = filtered.filter(a =>
        a.auditType.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.auditor.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredAudits(filtered);
  };

  const stats = {
    total: audits.length,
    completed: audits.filter(a => a.status === 'Completed').length,
    scheduled: audits.filter(a => a.status === 'Scheduled').length,
    averageScore: audits.filter(a => a.score).reduce((sum, a) => sum + a.score, 0) / audits.filter(a => a.score).length || 0,
    upcoming: audits.filter(a => {
      if (a.status === 'Scheduled' && a.scheduledDate) {
        return new Date(a.scheduledDate) >= new Date();
      }
      return false;
    }).length
  };

  const categories = {
    medication: audits.filter(a => a.category === 'medication'),
    infection: audits.filter(a => a.category === 'infection'),
    resident: audits.filter(a => a.category === 'resident'),
    safety: audits.filter(a => a.category === 'safety')
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3 mb-2">
              <ClipboardList className="w-8 h-8" />
              Audit Management
            </h1>
            <p className="text-purple-100 text-lg">Comprehensive audit tracking and compliance monitoring</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowNewModal(true)}
              className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg font-semibold flex items-center gap-2 transition-all"
            >
              <Plus className="w-4 h-4" />
              Schedule Audit
            </button>
            <button
              onClick={() => setShowExportModal(true)}
              className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg font-semibold flex items-center gap-2 transition-all"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-purple-100 rounded-xl">
              <ClipboardList className="w-6 h-6 text-purple-600" />
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-gray-800">{stats.total}</div>
              <div className="text-xs text-gray-500">Total Audits</div>
            </div>
          </div>
          <h3 className="font-bold text-gray-800">All Time</h3>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-green-200">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-green-100 rounded-xl">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-green-600">{stats.completed}</div>
              <div className="text-xs text-gray-500">Completed</div>
            </div>
          </div>
          <h3 className="font-bold text-gray-800">Finished</h3>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-blue-200">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-blue-100 rounded-xl">
              <Calendar className="w-6 h-6 text-blue-600" />
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-blue-600">{stats.scheduled}</div>
              <div className="text-xs text-gray-500">Scheduled</div>
            </div>
          </div>
          <h3 className="font-bold text-gray-800">Upcoming</h3>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-indigo-200">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-indigo-100 rounded-xl">
              <Award className="w-6 h-6 text-indigo-600" />
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-indigo-600">{stats.averageScore.toFixed(1)}%</div>
              <div className="text-xs text-gray-500">Avg Score</div>
            </div>
          </div>
          <h3 className="font-bold text-gray-800">Performance</h3>
        </div>
      </div>

      {/* Category Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {Object.entries(categories).map(([key, categoryAudits]) => {
          const avgScore = categoryAudits.filter(a => a.score).reduce((sum, a) => sum + a.score, 0) / categoryAudits.filter(a => a.score).length || 0;
          const completed = categoryAudits.filter(a => a.status === 'Completed').length;
          const pending = categoryAudits.filter(a => a.status === 'Scheduled').length;
          
          return (
            <div key={key} className="bg-white rounded-xl p-6 shadow-lg border-2 border-gray-200 hover:border-purple-300 transition-all">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-800 capitalize">{key}</h3>
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div className="text-3xl font-bold text-gray-800 mb-2">{avgScore.toFixed(1)}%</div>
              <div className="text-sm text-gray-600 mb-3">Average Score</div>
              <div className="flex justify-between text-sm">
                <span className="text-green-600 font-semibold">✓ {completed}</span>
                <span className="text-yellow-600 font-semibold">⏳ {pending}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search audits by type or auditor..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="completed">Completed</option>
            <option value="scheduled">Scheduled</option>
            <option value="in progress">In Progress</option>
          </select>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="all">All Categories</option>
            <option value="medication">Medication</option>
            <option value="infection">Infection Control</option>
            <option value="resident">Resident Care</option>
            <option value="safety">Safety</option>
          </select>
        </div>
      </div>

      {/* Audits Table */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <ClipboardList className="w-6 h-6 text-purple-600" />
              Audit History ({filteredAudits.length})
            </h3>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left py-4 px-6 font-bold text-gray-700">Audit Type</th>
                <th className="text-left py-4 px-6 font-bold text-gray-700">Auditor</th>
                <th className="text-center py-4 px-6 font-bold text-gray-700">Date</th>
                <th className="text-center py-4 px-6 font-bold text-gray-700">Score</th>
                <th className="text-center py-4 px-6 font-bold text-gray-700">Status</th>
                <th className="text-center py-4 px-6 font-bold text-gray-700">Findings</th>
                <th className="text-center py-4 px-6 font-bold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredAudits.map((audit) => (
                <tr key={audit.id} className="hover:bg-gray-50 transition-colors">
                  <td className="py-4 px-6">
                    <div className="font-semibold text-gray-800">{audit.auditType}</div>
                    <div className="text-xs text-gray-500 capitalize">{audit.category}</div>
                  </td>
                  <td className="py-4 px-6">
                    <div className="text-gray-700">{audit.auditor}</div>
                    <div className="text-xs text-gray-500">{audit.auditorOrganization}</div>
                  </td>
                  <td className="py-4 px-6 text-center">
                    <div className="text-gray-700">{new Date(audit.auditDate).toLocaleDateString()}</div>
                    {audit.nextAuditDate && (
                      <div className="text-xs text-gray-500">Next: {new Date(audit.nextAuditDate).toLocaleDateString()}</div>
                    )}
                  </td>
                  <td className="py-4 px-6 text-center">
                    {audit.score ? (
                      <div>
                        <div className={`text-2xl font-bold ${
                          audit.score >= 95 ? 'text-green-600' :
                          audit.score >= 85 ? 'text-blue-600' :
                          'text-yellow-600'
                        }`}>
                          {audit.score}%
                        </div>
                        <div className="text-xs text-gray-500">of {audit.maxScore}</div>
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="py-4 px-6 text-center">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      audit.status === 'Completed' ? 'bg-green-100 text-green-800' :
                      audit.status === 'Scheduled' ? 'bg-blue-100 text-blue-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {audit.status}
                    </span>
                  </td>
                  <td className="py-4 px-6 text-center">
                    {audit.findings ? (
                      <div>
                        <div className="font-semibold text-gray-800">{audit.findings.length}</div>
                        <div className="text-xs text-gray-500">findings</div>
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => setSelectedAudit(audit)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {audit.reportUrl && (
                        <button
                          onClick={() => showToast('Downloading audit report...', 'info')}
                          className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-all"
                          title="Download Report"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Audit Detail Modal */}
      {selectedAudit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-2xl p-6 max-w-3xl w-full mx-4 my-8 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">
                {selectedAudit.auditType} - {selectedAudit.id}
              </h2>
              <button
                onClick={() => setSelectedAudit(null)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-6">
              {/* Key Information */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                  <div className="text-sm text-gray-600 mb-1">Audit Date</div>
                  <div className="text-lg font-bold text-purple-600">
                    {new Date(selectedAudit.auditDate).toLocaleDateString()}
                  </div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <div className="text-sm text-gray-600 mb-1">Score</div>
                  <div className="text-lg font-bold text-green-600">
                    {selectedAudit.score ? `${selectedAudit.score}%` : 'N/A'}
                  </div>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <div className="text-sm text-gray-600 mb-1">Auditor</div>
                  <div className="text-lg font-bold text-blue-600">{selectedAudit.auditor}</div>
                  <div className="text-xs text-gray-500">{selectedAudit.auditorOrganization}</div>
                </div>
                <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                  <div className="text-sm text-gray-600 mb-1">Status</div>
                  <div className="text-lg font-bold text-indigo-600">{selectedAudit.status}</div>
                </div>
              </div>

              {/* Scope */}
              <div>
                <h4 className="font-semibold mb-2">Audit Scope</h4>
                <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">{selectedAudit.scope}</p>
              </div>

              {/* Findings */}
              {selectedAudit.findings && selectedAudit.findings.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Findings</h4>
                  <div className="space-y-2">
                    {selectedAudit.findings.map((finding, index) => (
                      <div key={index} className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <div className="font-semibold text-gray-800">{finding.description}</div>
                            <div className="text-xs text-gray-600 mt-1">{finding.recommendation}</div>
                          </div>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            finding.severity === 'Minor' ? 'bg-yellow-100 text-yellow-800' :
                            finding.severity === 'Major' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {finding.severity}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500">
                          Status: {finding.status} {finding.addressedDate && `- ${new Date(finding.addressedDate).toLocaleDateString()}`}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recommendations */}
              {selectedAudit.recommendations && selectedAudit.recommendations.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Recommendations</h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 bg-purple-50 p-4 rounded-lg">
                    {selectedAudit.recommendations.map((rec, index) => (
                      <li key={index}>{rec}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Strengths */}
              {selectedAudit.strengths && selectedAudit.strengths.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Strengths</h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 bg-green-50 p-4 rounded-lg">
                    {selectedAudit.strengths.map((strength, index) => (
                      <li key={index}>{strength}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Next Audit */}
              {selectedAudit.nextAuditDate && (
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-2 text-blue-800">
                    <Calendar className="w-5 h-5" />
                    <span className="font-semibold">
                      Next Audit: {new Date(selectedAudit.nextAuditDate).toLocaleDateString()} ({selectedAudit.nextAuditType})
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* New Audit Modal */}
      {showNewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-purple-800 flex items-center gap-2">
                <Plus className="w-6 h-6 text-purple-600" />
                Schedule New Audit
              </h2>
              <button
                onClick={() => setShowNewModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Audit Type</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500">
                    <option>Medication Management</option>
                    <option>Infection Control</option>
                    <option>Resident Care</option>
                    <option>Safety & Security</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Scheduled Date</label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    defaultValue={new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Audit Scope</label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 h-24"
                  placeholder="Describe the scope and objectives..."
                ></textarea>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowNewModal(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    showToast('Audit scheduled successfully', 'success');
                    setShowNewModal(false);
                    loadData();
                  }}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold"
                >
                  Schedule Audit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                <Download className="w-6 h-6 text-purple-600" />
                Export Audit Data
              </h2>
              <button
                onClick={() => setShowExportModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={() => {
                  const csv = generateCSV();
                  downloadFile(csv, `audits-${selectedFacility}.csv`, 'text/csv');
                  setShowExportModal(false);
                }}
                className="w-full px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold flex items-center justify-center gap-2"
              >
                <FileText className="w-5 h-5" />
                Export as CSV
              </button>
              <button
                onClick={() => {
                  const json = JSON.stringify(audits, null, 2);
                  downloadFile(json, `audits-${selectedFacility}.json`, 'application/json');
                  setShowExportModal(false);
                }}
                className="w-full px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-semibold flex items-center justify-center gap-2"
              >
                <FileText className="w-5 h-5" />
                Export as JSON
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  function generateCSV() {
    const headers = ['ID', 'Type', 'Date', 'Auditor', 'Score', 'Status', 'Findings'];
    const rows = audits.map(a => [
      a.id,
      a.auditType,
      a.auditDate,
      a.auditor,
      a.score || 'N/A',
      a.status,
      a.findings ? a.findings.length : 0
    ].join(','));
    return [headers.join(','), ...rows].join('\n');
  }

  function downloadFile(content, filename, mimeType) {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};

export default AuditsDashboard;
